<template>
    <LsInfoRow
        class="ls-info-row-status"
        :left-image="require('./assets/status.svg')"
        label="Status"
        :value="value"
    >
        <img
            v-if="isManualPrice && quoteLoading"
            class="ls-info-row-status--spinner"
            :src="require('../../assets/spinner.svg')"
            test-id="quote-loading"
        />
        <v-popover
            v-else-if="isManualPrice && hasScheduleEventStatus"
            test-id="manual-price-pop-over"
        >
            <img :src="require('../../assets/info.svg')" />
            <template slot="popover">
                <div class="ls-info-row-status--popover">
                    <div
                        v-if="hasProDecline"
                        class="ls-info-row-status--section"
                        test-id="pro-decline-section"
                    >
                        <div>
                            <span>Provider decline reasons:</span><br />
                            <span
                                v-for="reason in proDeclineReasonItems"
                                :key="reason.value"
                                class="ls-info-row-status--decline-reason"
                                test-id="pro-decline-reason"
                                >{{ reason.label }}</span
                            >
                        </div>
                        <div v-if="proDeclineDetails">
                            <span>Provider decline details:</span><br />
                            <span test-id="pro-decline-details">{{ proDeclineDetails }}</span>
                        </div>
                    </div>

                    <div
                        v-if="hasCustomerDecline"
                        class="ls-info-row-status--section"
                        test-id="customer-decline-section"
                    >
                        <div v-if="customerDeclineReasonLabel">
                            <span>Customer decline reason:</span><br />
                            <span test-id="customer-decline-reason">{{
                                customerDeclineReasonLabel
                            }}</span>
                        </div>
                        <div v-if="customerDeclineDetails">
                            <span>Customer decline details:</span><br />
                            <span test-id="customer-decline-details">{{
                                customerDeclineDetails
                            }}</span>
                        </div>
                    </div>

                    <div>
                        <span>Action on:</span><br />
                        <span test-id="manual-price-status-values">{{ manualPriceTimestamp }}</span>
                    </div>
                </div>
            </template>
        </v-popover>
        <v-popover v-else-if="!isManualPrice">
            <img :src="require('../../assets/info.svg')" test-id="info-image" />
            <template slot="popover">
                <div class="ls-info-row-status--popover">
                    <div
                        v-if="status === 'cancelled'"
                        class="ls-info-row-status--section"
                        test-id="cancelled-section"
                    >
                        <div>
                            <span>Effective date:</span><br />
                            <span test-id="cancelled-value">{{
                                service.requested_start_date | date
                            }}</span>
                        </div>
                        <div>
                            <span>Responsible party:</span><br />
                            <span test-id="cancelled-value">{{
                                getResponsibleParty || 'N/A'
                            }}</span>
                        </div>
                        <div>
                            <span>Reason:</span><br />
                            <span test-id="cancelled-value">{{
                                service.status_reason || 'N/A'
                            }}</span>
                        </div>
                        <div v-if="service.status_details">
                            <span>Additional notes for cancelling:</span><br />
                            <span test-id="cancelled-value">{{
                                service.status_details || 'N/A'
                            }}</span>
                        </div>
                    </div>

                    <div v-if="status === 'declined'" class="ls-info-row-status--section">
                        <div>
                            <span>Reason for declining:</span><br />
                            <span test-id="declined-value">{{ declinedReasons }}</span>
                        </div>

                        <div>
                            <span>Anything we should know:</span><br />
                            <span test-id="declined-value">
                                {{ lastDeclined.description || 'N/A' }}</span
                            >
                        </div>
                    </div>

                    <div v-if="status === 'rejected'">
                        <span>Additional notes for turning down:</span><br />
                        <span>{{ serviceType.status_details || 'N/A' }}</span>
                    </div>

                    <div>
                        <span>Action by:</span><br />
                        <span>{{ getActionBy }}</span>
                    </div>
                    <div>
                        <span>Action on:</span><br />
                        <span>{{ getTimestamp }}</span>
                    </div>
                </div>
            </template>
        </v-popover>
    </LsInfoRow>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import { lsFilters } from 'ls-vue-common';
import LsInfoRow from '../LsInfoRow/LsInfoRow.vue';
import {
    LsCustomerDeclineReasonLabels,
    LsProDeclineReasonLabels,
    LsQuoteDeclineReasons,
    LsScheduleEventCancelledReasonLabels,
    LsScheduleEventStatusLabels,
} from './LsInfoRowStatus.constants';
import { lsHelpers } from '@/helpers';

const MANUAL_PRICE = 'manual_price';
const MANUAL_QUOTE = 'manualquote';
const CANCELLED = 'CANCELLED';
// Matches the placeholder the sibling pricing row shows.
const EMPTY_DISPLAY = '- - - -';

// Falls back so an unmapped enum value never reaches the screen raw.
const declineReasonLabel = ({ labels, value }) => labels[value] || _.startCase(_.toLower(value));

export default {
    name: 'LsInfoRowStatus',
    components: {
        LsInfoRow,
    },
    props: {
        service: VueTypes.shape({
            type: VueTypes.string,
            __typename: VueTypes.string.isRequired,
            status: VueTypes.string,
            status_fault: VueTypes.oneOfType([VueTypes.string, VueTypes.oneOfType([null])]),
            status_reason: VueTypes.oneOfType([VueTypes.string, VueTypes.oneOfType([null])]),
            status_details: VueTypes.oneOfType([VueTypes.string, VueTypes.oneOfType([null])]),
            requested_start_date: VueTypes.oneOfType([VueTypes.string, VueTypes.oneOfType([null])]),
            manualquote: VueTypes.shape({
                status: VueTypes.string.isRequired,
                status_set_at: VueTypes.string.isRequired,
                status_details: VueTypes.oneOfType([VueTypes.string, VueTypes.oneOfType([null])]),
                price: VueTypes.oneOfType([
                    VueTypes.shape({
                        accepted: VueTypes.string,
                    }).loose,
                    VueTypes.oneOfType([null]),
                ]),
                declines: VueTypes.arrayOf(
                    VueTypes.shape({
                        description: VueTypes.oneOfType([
                            VueTypes.string,
                            VueTypes.oneOfType([null]),
                        ]),
                        created_at: VueTypes.string,
                        contractor: VueTypes.shape({
                            user: VueTypes.shape({
                                first_name: VueTypes.string.isRequired,
                                last_name: VueTypes.string.isRequired,
                            }).loose.isRequired,
                        }).loose.isRequired,
                    }).loose
                ),
                status_user: VueTypes.oneOfType([
                    VueTypes.shape({
                        is_admin: VueTypes.bool.isRequired,
                        contractor_id: VueTypes.oneOfType([
                            VueTypes.number,
                            VueTypes.oneOfType([null]),
                        ]),
                    }).loose,
                    VueTypes.oneOfType([null]),
                ]),
            }).loose,
        }).isRequired.loose,
        quote: VueTypes.oneOfType([
            VueTypes.shape({
                customerDeclineReason: VueTypes.oneOfType([
                    VueTypes.string,
                    VueTypes.oneOfType([null]),
                ]),
                customerDeclineDetails: VueTypes.oneOfType([
                    VueTypes.string,
                    VueTypes.oneOfType([null]),
                ]),
                proDeclineReasons: VueTypes.oneOfType([
                    VueTypes.arrayOf(VueTypes.string),
                    VueTypes.oneOfType([null]),
                ]),
                proDeclineDetails: VueTypes.oneOfType([
                    VueTypes.string,
                    VueTypes.oneOfType([null]),
                ]),
                scheduleEvent: VueTypes.oneOfType([
                    VueTypes.shape({
                        status: VueTypes.string,
                        statusReason: VueTypes.oneOfType([
                            VueTypes.string,
                            VueTypes.oneOfType([null]),
                        ]),
                        statusSetAt: VueTypes.oneOfType([
                            VueTypes.string,
                            VueTypes.oneOfType([null]),
                        ]),
                    }).loose,
                    VueTypes.oneOfType([null]),
                ]),
            }).loose,
            VueTypes.oneOfType([null]),
        ]).def(null),
        quoteLoading: VueTypes.bool.def(false),
    },
    computed: {
        isManualQuote() {
            return _.get(this.service, 'type') === MANUAL_QUOTE;
        },
        // Independent fields, so yield to the legacy rail if both read true.
        isManualPrice() {
            return (
                !this.isManualQuote && _.get(this.service, 'service.price_type') === MANUAL_PRICE
            );
        },
        scheduleEventStatus() {
            return _.get(this.quote, 'scheduleEvent.status');
        },
        hasScheduleEventStatus() {
            return !_.isNil(this.scheduleEventStatus);
        },
        manualPriceValue() {
            if (this.quoteLoading || !this.hasScheduleEventStatus) {
                return EMPTY_DISPLAY;
            }

            if (this.scheduleEventStatus === CANCELLED) {
                const reason = _.get(this.quote, 'scheduleEvent.statusReason');
                return (
                    LsScheduleEventCancelledReasonLabels[reason] ||
                    LsScheduleEventCancelledReasonLabels.cancelled
                );
            }

            return (
                LsScheduleEventStatusLabels[this.scheduleEventStatus] ||
                _.startCase(_.toLower(this.scheduleEventStatus))
            );
        },
        isCancelledManualPrice() {
            return this.isManualPrice && this.scheduleEventStatus === CANCELLED;
        },
        // The raw value rides along because two enum values can start-case to one label.
        proDeclineReasonItems() {
            return _.map(_.get(this.quote, 'proDeclineReasons') || [], (value) => ({
                value,
                label: declineReasonLabel({ labels: LsProDeclineReasonLabels, value }),
            }));
        },
        // A Provider always declines with reasons; a customer may send details alone.
        hasProDecline() {
            return this.isCancelledManualPrice && !_.isEmpty(this.proDeclineReasonItems);
        },
        proDeclineDetails() {
            return _.get(this.quote, 'proDeclineDetails');
        },
        customerDeclineReasonLabel() {
            const value = _.get(this.quote, 'customerDeclineReason');

            return _.isNil(value)
                ? null
                : declineReasonLabel({ labels: LsCustomerDeclineReasonLabels, value });
        },
        customerDeclineDetails() {
            return _.get(this.quote, 'customerDeclineDetails');
        },
        // The API cannot produce both declines on one quote.
        hasCustomerDecline() {
            return (
                this.isCancelledManualPrice &&
                !this.hasProDecline &&
                Boolean(this.customerDeclineReasonLabel || this.customerDeclineDetails)
            );
        },
        manualPriceTimestamp() {
            const statusSetAt = _.get(this.quote, 'scheduleEvent.statusSetAt');

            return _.isNil(statusSetAt)
                ? EMPTY_DISPLAY
                : lsHelpers.parseTimestamp({ timestamp: statusSetAt });
        },
        serviceType() {
            return _.get(this.service, `${[this.service.type]}`) || this.service;
        },
        status() {
            return this.serviceType.status;
        },
        value() {
            if (this.isManualPrice) {
                return this.manualPriceValue;
            }
            if (this.status === 'new') {
                return 'Quote created';
            }
            if (this.status === 'ready') {
                return 'Quote priced';
            }
            if (this.status === 'approved') {
                return 'Quote accepted';
            }
            if (this.status === 'authorized') {
                return `${_.startCase(this.serviceType.price.accepted)} quote accepted`;
            }
            return `Quote ${this.status}`;
        },
        lastDeclined() {
            return _.last(this.serviceType.declines);
        },
        userTypeString() {
            const { status_user } = this.serviceType;

            if (status_user.is_admin) {
                return '(Admin)';
            }
            if (status_user.contractor_id) {
                return '(Provider)';
            }
            return '(Customer)';
        },
        getActionBy() {
            if (this.status === 'expired') {
                return 'System';
            }

            if (this.status === 'declined') {
                return `${lsFilters.fullName({
                    user: this.lastDeclined.contractor.user,
                })} (Provider)`;
            }

            const { status_user } = this.serviceType;

            if (status_user) {
                return `${lsFilters.fullName({ user: status_user })} ${this.userTypeString}`;
            }

            return 'Unknown';
        },
        getTimestamp() {
            return lsHelpers.parseTimestamp({
                timestamp:
                    this.status === 'declined'
                        ? this.lastDeclined.created_at
                        : this.serviceType.status_set_at,
            });
        },
        getResponsibleParty() {
            return _.upperFirst(this.service.status_fault);
        },
        declinedReasons() {
            const reasons = _.pick(this.lastDeclined, _.keys(LsQuoteDeclineReasons));
            const reasonsString = _.reduce(
                reasons,
                (result, value, key) => {
                    if (value) {
                        result.push(LsQuoteDeclineReasons[key]);
                    }
                    return result;
                },
                []
            ).join(', ');

            return _.upperFirst(reasonsString);
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-info-row-status--popover {
    width: 240px;
    color: $secondary-dark;
    line-height: 16px;
    div:not(.ls-info-row-status--section) {
        &:not(:last-child) {
            margin-bottom: 10px;
        }
        :first-child {
            font-weight: $bold;
        }
    }
}

.ls-info-row-status--spinner {
    width: 16px;
    height: 16px;
    animation: rotation 2s infinite linear;

    @keyframes rotation {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(359deg);
        }
    }
}

// Each reason sits on its own line.
.ls-info-row-status--decline-reason {
    display: block;
}

.ls-info-row-status--section {
    div {
        margin-bottom: 10px;
        :first-child {
            font-weight: $bold;
        }
    }
}
</style>
